"use client";

import * as React from "react";
import { signIn } from "next-auth/react";
import { toast } from "sonner";
import {
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Smartphone,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isValidPhone } from "@/lib/format";
import { pushView } from "@/stores/useAppStore";

export default function LoginForm() {
  const [phone, setPhone] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [touched, setTouched] = React.useState<{
    phone?: boolean;
    password?: boolean;
  }>({});

  const phoneError = touched.phone && !isValidPhone(phone) ? true : false;
  const passwordError = touched.password && password.length === 0 ? true : false;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched({ phone: true, password: true });

    if (!isValidPhone(phone)) {
      toast.error("شماره موبایل معتبر نیست");
      return;
    }
    if (!password) {
      toast.error("رمز عبور را وارد کنید");
      return;
    }

    setLoading(true);
    try {
      const res = await signIn("credentials", {
        phone,
        password,
        redirect: false,
      });
      if (!res || res.error) {
        toast.error("شماره موبایل یا رمز عبور نادرست است.");
      } else {
        toast.success("خوش آمدید!");
        // Role-aware redirect: admins land on the admin panel, users on their
        // dashboard. A short delay so the toast is visible before the reload.
        let role = "";
        try {
          const s = await fetch("/api/session", { cache: "no-store" });
          const sj = await s.json();
          role = sj?.user?.role ?? "";
        } catch {
          role = "";
        }
        setTimeout(() => {
          window.location.assign(role === "ADMIN" ? "/?view=admin" : "/?view=dashboard");
        }, 450);
      }
    } catch (err) {
      console.error(err);
      toast.error("خطایی رخ داد. دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="w-full max-w-md rounded-2xl border-border/60 shadow-lg">
      <CardHeader className="gap-2">
        <div className="mb-1 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => pushView("landing")}
          >
            <ArrowRight className="size-4" />
            بازگشت به خانه
          </Button>
        </div>
        <CardTitle className="text-2xl font-bold tracking-tight">
          ورود به حساب
        </CardTitle>
        <CardDescription>برای ادامه وارد شوید</CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor="login-phone">شماره موبایل</Label>
            <div className="relative">
              <Smartphone className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="login-phone"
                name="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                dir="ltr"
                placeholder="09121111111"
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value.replace(/[^\d]/g, ""))
                }
                onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                aria-invalid={phoneError}
                aria-describedby={phoneError ? "login-phone-error" : undefined}
                disabled={loading}
                className="h-11 rounded-xl pr-10 text-left text-base md:text-base"
              />
            </div>
            {phoneError && (
              <p
                id="login-phone-error"
                className="text-xs text-destructive"
                role="alert"
              >
                شماره موبایل معتبر نیست
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="login-password">رمز عبور</Label>
            <div className="relative">
              <Lock className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="login-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                dir="ltr"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                aria-invalid={passwordError}
                aria-describedby={
                  passwordError ? "login-password-error" : undefined
                }
                disabled={loading}
                className="h-11 rounded-xl pr-10 pl-10 text-left text-base md:text-base"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={showPassword ? "پنهان کردن رمز" : "نمایش رمز"}
                className="absolute left-1 top-1/2 size-8 -translate-y-1/2 text-muted-foreground hover:bg-transparent hover:text-foreground"
                onClick={() => setShowPassword((s) => !s)}
                tabIndex={-1}
                disabled={loading}
              >
                {showPassword ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </Button>
            </div>
            {passwordError && (
              <p
                id="login-password-error"
                className="text-xs text-destructive"
                role="alert"
              >
                رمز عبور را وارد کنید
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="h-11 w-full rounded-xl text-base font-semibold"
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                در حال ورود...
              </>
            ) : (
              "ورود"
            )}
          </Button>
        </form>
      </CardContent>

      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground">
          حساب ندارید؟{" "}
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-primary"
            onClick={() => pushView("register")}
            disabled={loading}
          >
            ثبت‌نام کنید
          </Button>
        </p>
      </CardFooter>
    </Card>
  );
}
